import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsibleMultiSelect } from "@/components/ResponsibleMultiSelect";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { da } from "date-fns/locale";
import { daDate, isClosedDate, holidayInfo, holidayLabel } from "@/lib/date";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ChevronUp, ChevronDown, Trash2, Plus, CalendarDays, Building2, Users, FileStack, FileText, Layers, AlertTriangle, Info } from "lucide-react";

const roleLabel = { admin: "Administrator", projektleder: "Projektleder", medarbejder: "Medarbejder" };
const NONE = "__none__";

function addDays(iso, days) {
  if (!iso) return null;
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function CreateProject() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(false);
  const [form, setForm] = useState({ template_id: "", name: "", customer_id: "", project_manager_id: "", start_date: "", description: "", customer_contact: null });
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    api.get("/templates", { params: { active_only: true } }).then((r) => setTemplates(r.data));
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.active !== false)));
  }, []);

  const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "da", { sensitivity: "base" });
  const sortedCustomers = [...customers].sort(byName);
  const activeUsers = [...users].sort(byName);
  const salgUsers = users.filter((u) => u.active !== false && (u.role === "admin" || (u.departments || []).includes("salg"))).sort(byName);
  const managers = users.filter((u) => u.role === "admin" || (u.role === "projektleder" && (u.departments || []).includes("salg"))).sort(byName);
  const selectedTemplate = useMemo(() => templates.find((t) => t.id === form.template_id), [templates, form.template_id]);
  const selCust = customers.find((c) => c.id === form.customer_id);
  const custContacts = selCust?.contacts || [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const chooseTemplate = (t) => {
    set("template_id", t.id);
    const activeSteps = (t.steps || []).filter((s) => s.active).sort((a, b) => a.sort_order - b.sort_order);
    setSteps(activeSteps.map((s, i) => ({
      key: `t-${s.id}`, source_template_step_id: s.id, name: s.name, description: s.description || "",
      selected: s.default_selected !== false, duration_days: s.default_duration_days || 0,
      responsible_user_ids: [], responsible_contact_name: "", default_role: s.default_responsible_role, sort_order: i,
    })));
  };

  const updateStep = (idx, patch) => setSteps((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const moveStep = (idx, dir) => setSteps((prev) => {
    const j = idx + dir; if (j < 0 || j >= prev.length) return prev;
    const next = [...prev]; [next[idx], next[j]] = [next[j], next[idx]];
    return next.map((s, i) => ({ ...s, sort_order: i }));
  });
  const removeStep = (idx) => setSteps((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sort_order: i })));
  const addStep = () => setSteps((prev) => [...prev, { key: `n-${Date.now()}`, source_template_step_id: null, name: "", description: "", selected: true, duration_days: 0, responsible_user_ids: [], responsible_contact_name: "", sort_order: prev.length }]);

  const selected = steps.filter((s) => s.selected);
  const totalDuration = selected.reduce((a, s) => a + (parseInt(s.duration_days) || 0), 0);
  const today = new Date().toISOString().slice(0, 10);
  const schedule = useMemo(() => {
    const map = {};
    if (!form.start_date) return { map, computedStart: null };
    const toISO = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const isWork = (dt) => dt.getDay() !== 0 && dt.getDay() !== 6 && !isClosedDate(dt);
    const prevOpen = (dt) => { while (!isWork(dt)) dt.setDate(dt.getDate() - 1); return dt; };
    const [sy, sm, sd] = form.start_date.slice(0, 10).split("-").map(Number);
    let cursor = new Date(sy, sm - 1, sd);
    const sel = steps.filter((s) => s.selected);
    for (let i = sel.length - 1; i >= 0; i--) {
      const dur = parseInt(sel[i].duration_days) || 0;
      const end = prevOpen(new Date(cursor));
      const start = new Date(end);
      let rem = dur;
      while (rem > 0) { start.setDate(start.getDate() - 1); if (isWork(start)) rem -= 1; }
      map[sel[i].key] = { start: toISO(start), end: toISO(end) };
      cursor = start;
    }
    return { map, computedStart: sel.length ? map[sel[0].key].start : null };
  }, [steps, form.start_date]);
  const startBeforeToday = schedule.computedStart && schedule.computedStart < today;
  const missingResponsible = selected.filter((s) => !(s.responsible_user_ids && s.responsible_user_ids.length) && !s.responsible_contact_name).length;

  const doSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        template_id: form.template_id, name: form.name, customer_id: form.customer_id || null,
        project_manager_id: form.project_manager_id || null, start_date: form.start_date || null,
        description: form.description, customer_contact: form.customer_contact,
        steps: steps.map((s, i) => ({
          source_template_step_id: s.source_template_step_id, name: s.name, description: s.description,
          selected: s.selected, duration_days: parseInt(s.duration_days) || 0,
          responsible_user_ids: s.responsible_user_ids || [], responsible_contact_name: s.responsible_contact_name || null, sort_order: i, status: "Ikke startet",
        })),
      };
      const { data } = await api.post("/projects", payload);
      toast.success("Projekt oprettet", { description: `${data.name} med ${data.total_tasks} trin.` });
      nav(`/projekt/${data.id}`);
    } catch (e) {
      toast.error("Kunne ikke oprette projekt", { description: formatApiError(e.response?.data?.detail) });
    } finally { setSaving(false); setWarn(false); }
  };

  const submit = () => {
    if (!form.name.trim()) return toast.error("Projektet skal have et navn");
    if (!form.project_manager_id) return toast.error("Vælg en projektansvarlig");
    if (selected.length === 0) return toast.error("Vælg mindst ét projekttrin");
    if (selected.some((s) => !s.name.trim())) return toast.error("Alle valgte trin skal have et navn");
    if (missingResponsible > 0) { setWarn(true); return; }
    doSubmit();
  };

  const canNext = step === 1 ? form.template_id : step === 2 ? (form.name.trim() && form.project_manager_id) : true;
  const stepLabels = ["Vælg skabelon", "Projektdetaljer", "Projekttrin"];

  return (
    <div className="rise-in max-w-4xl">
      <Button variant="ghost" size="sm" onClick={() => nav("/projekter")} className="mb-4 -ml-2 text-muted-foreground rounded-xl" data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Tilbage</Button>
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-8 text-[#1A1919]">Opret nyt projekt</h1>

      <div className="flex items-center gap-2 mb-8">
        {stepLabels.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold border [transition-property:color,background-color,border-color] duration-200 ${step > i + 1 ? "bg-primary text-white border-primary" : step === i + 1 ? "border-primary text-primary bg-[#F1DEDA]" : "border-[#EBE8E3] text-muted-foreground bg-white"}`}>{step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}</div>
            <span className={`text-sm font-medium ${step === i + 1 ? "text-[#1A1919]" : "text-muted-foreground"} hidden sm:inline`}>{s}</span>
            {i < stepLabels.length - 1 && <div className="w-6 sm:w-10 h-px bg-[#EBE8E3]" />}
          </div>
        ))}
      </div>

      <div className="pcard p-6 sm:p-8">
        {step === 1 && (
          <div className="space-y-3">
            <p className="label-caps mb-4">Aktive projektskabeloner</p>
            {templates.length === 0 && <p className="text-muted-foreground text-sm">Ingen aktive skabeloner.</p>}
            {templates.map((t) => {
              const active = form.template_id === t.id;
              const stepCount = t.steps?.filter((s) => s.active).length || 0;
              const days = t.steps?.filter((s) => s.active).reduce((a, s) => a + (s.default_duration_days || 0), 0) || 0;
              return (
                <button key={t.id} data-testid={`template-option-${t.id}`} onClick={() => chooseTemplate(t)}
                  className={`w-full text-left p-5 border rounded-2xl flex items-start gap-4 [transition-property:background-color,border-color] duration-200 ${active ? "border-[#A83232] bg-[#F1DEDA]" : "border-[#EBE8E3] hover:bg-[#F3F1EC]"}`}>
                  <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-[#A83232] text-white" : "bg-[#F3F1EC] text-stone-500"}`}><FileStack className="h-5 w-5" strokeWidth={1.75} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#1A1919]">{t.name}</span>
                      {t.project_type && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white border border-[#EBE8E3] text-stone-500">{t.project_type}</span>}
                    </div>
                    {t.description && <p className="text-sm text-muted-foreground mt-1.5">{t.description}</p>}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2.5">
                      <span className="inline-flex items-center gap-1"><Layers className="h-3.5 w-3.5" strokeWidth={1.75} /> {stepCount} trin</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} /> {days} dage</span>
                    </div>
                  </div>
                  {active && <Check className="h-5 w-5 text-[#A83232] shrink-0" />}
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-sm font-medium">Projektnavn *</Label>
              <div className="relative">
                <FileStack className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" strokeWidth={1.75} />
                <Input data-testid="input-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="fx Stålramme til Vestas Hal B" className="rounded-xl h-11 pl-9 bg-white" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Kunde</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none z-10" strokeWidth={1.75} />
                <Select value={form.customer_id} onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v, customer_contact: null }))}>
                  <SelectTrigger data-testid="select-customer" className="rounded-xl h-11 pl-9 bg-white"><SelectValue placeholder="Vælg kunde" /></SelectTrigger>
                  <SelectContent>{sortedCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {custContacts.length > 0 && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-sm font-medium">Kontaktperson hos kunde</Label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none z-10" strokeWidth={1.75} />
                  <Select value={form.customer_contact ? String(custContacts.findIndex((c) => c.name === form.customer_contact.name && (c.email || "") === (form.customer_contact.email || ""))) : NONE}
                    onValueChange={(v) => set("customer_contact", v === NONE ? null : custContacts[parseInt(v)])}>
                    <SelectTrigger data-testid="select-contact" className="rounded-xl h-11 pl-9 bg-white"><SelectValue placeholder="Vælg kontaktperson" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Ingen</SelectItem>
                      {custContacts.map((c, i) => <SelectItem key={i} value={String(i)}>{c.name || c.email || `Kontakt ${i + 1}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Projektansvarlig *</Label>
              <UserSingleSelect users={managers} value={form.project_manager_id || null} onChange={(v) => set("project_manager_id", v || "")} placeholder="Vælg projektansvarlig" testid="select-manager" className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Deadline</Label>
              <DanishDatePicker value={form.start_date} onChange={(iso) => set("start_date", iso)} testid="input-start" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-sm font-medium">Kort projektbeskrivelse</Label>
              <Textarea data-testid="input-description" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="rounded-xl bg-white" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="preview-section">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-[#F1DEDA] border border-[#E9C9C3] rounded-2xl p-5">
              <div>
                <p className="label-caps text-[#A83232]/70">Samlet forventet projektvarighed</p>
                <p className="text-2xl font-semibold mt-1 text-[#A83232]" data-testid="total-duration">{totalDuration} dage</p>
              </div>
              {form.start_date && (
                <div className="text-right">
                  <p className="label-caps text-stone-400">Deadline</p>
                  <p className="font-semibold mt-1 text-[#1A1919]" data-testid="deadline-preview">{daDate(form.start_date)}</p>
                  {schedule.computedStart && <p className="text-xs text-stone-400 mt-1">Beregnet start: <span className="font-medium text-[#1A1919]" data-testid="computed-start-preview">{daDate(schedule.computedStart)}</span></p>}
                </div>
              )}
            </div>
            {startBeforeToday && (
              <div className="flex items-start gap-2 text-sm text-[#806B61] bg-[#F4EFEC] border border-[#E6DDD6] rounded-2xl px-4 py-3" data-testid="start-before-today-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.75} />
                <span>Med den valgte deadline og de {totalDuration} afsatte dage bliver den beregnede startdato ({daDate(schedule.computedStart)}) <strong>før dags dato</strong>. Vælg en senere deadline eller reducér antal dage.</span>
              </div>
            )}
            {!form.start_date && (
              <div className="text-sm text-muted-foreground bg-[#FBFAF8] border border-[#EBE8E3] rounded-2xl px-4 py-3" data-testid="no-deadline-preview">Vælg en deadline på forrige trin for at se beregnede start- og slutdatoer pr. trin.</div>
            )}
            <p className="text-sm text-muted-foreground">Tilvælg/fravælg trin, ret navn, varighed, ansvarlig og rækkefølge. De valgte trin kopieres som selvstændige projekttrin.</p>

            <div className="space-y-2">
              {steps.map((s, idx) => (
                <div key={s.key} className={`border rounded-2xl p-4 bg-white ${s.selected ? "border-[#EBE8E3]" : "border-dashed border-[#EBE8E3] opacity-70"}`} data-testid={`wizard-step-${idx}`}>
                  <div className="flex items-start gap-2">
                    <Checkbox checked={s.selected} onCheckedChange={(c) => updateStep(idx, { selected: !!c })} className="mt-2.5 border-[#D6D3D1] data-[state=checked]:bg-[#A83232] data-[state=checked]:border-[#A83232]" data-testid={`wizard-step-check-${idx}`} />
                    <div className="flex flex-col">
                      <Button size="icon" variant="ghost" className="h-6 w-6 rounded-lg" disabled={idx === 0} onClick={() => moveStep(idx, -1)} data-testid={`wizard-step-up-${idx}`}><ChevronUp className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6 rounded-lg" disabled={idx === steps.length - 1} onClick={() => moveStep(idx, 1)} data-testid={`wizard-step-down-${idx}`}><ChevronDown className="h-4 w-4" /></Button>
                    </div>
                    <div className="flex-1 grid sm:grid-cols-12 gap-2">
                      <Input className="sm:col-span-5 rounded-xl" placeholder="Trinnavn" value={s.name} onChange={(e) => updateStep(idx, { name: e.target.value })} data-testid={`wizard-step-name-${idx}`} />
                      <div className="sm:col-span-2 flex items-center gap-1"><Input type="number" min="0" value={s.duration_days} onChange={(e) => updateStep(idx, { duration_days: e.target.value })} data-testid={`wizard-step-duration-${idx}`} className="rounded-xl" /><span className="text-xs text-muted-foreground">d</span></div>
                      <div className="sm:col-span-4">
                        <ResponsibleMultiSelect
                          users={salgUsers}
                          contactName={form.customer_contact?.name || null}
                          value={[...(s.responsible_user_ids || []), ...(s.responsible_contact_name ? ["__contact__"] : [])]}
                          onChange={(tokens) => updateStep(idx, { responsible_user_ids: tokens.filter((t) => t !== "__contact__"), responsible_contact_name: tokens.includes("__contact__") ? (form.customer_contact?.name || null) : "" })}
                          testid={`wizard-step-resp-${idx}`}
                          className="h-10"
                        />
                      </div>
                      <Button size="icon" variant="ghost" className="sm:col-span-1 text-destructive hover:text-destructive rounded-lg" onClick={() => removeStep(idx)} data-testid={`wizard-step-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  {s.selected && schedule.map[s.key] && (
                    <p className="text-xs text-muted-foreground mt-1 pl-9 flex items-center gap-1.5" data-testid={`wizard-step-dates-${idx}`}>
                      <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
                      {daDate(schedule.map[s.key].start)} → {daDate(schedule.map[s.key].end)}
                    </p>
                  )}
                  {s.selected && schedule.map[s.key] && (() => {
                    const hi = holidayInfo(schedule.map[s.key].start, schedule.map[s.key].end);
                    return hi ? (
                      <p className="text-xs text-[#806B61] mt-1 pl-9 flex items-center gap-1.5" data-testid={`wizard-step-holiday-${idx}`}>
                        <Info className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} /> {holidayLabel(hi)}
                      </p>
                    ) : null;
                  })()}
                  {s.selected && !form.start_date && <p className="text-xs text-muted-foreground mt-1 pl-9">Vælg en deadline for at se datoer</p>}
                </div>
              ))}
            </div>
            <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={addStep} data-testid="wizard-add-step"><Plus className="h-4 w-4 mr-1" /> Tilføj trin</Button>
            {missingResponsible > 0 && <p className="text-sm text-[#806B61] font-medium">{missingResponsible} projekttrin mangler en ansvarlig.</p>}
          </div>
        )}
      </div>

      <div className="flex justify-between mt-6">
        <Button variant="outline" className="rounded-xl border-[#EBE8E3]" disabled={step === 1} onClick={() => setStep((s) => s - 1)} data-testid="prev-step-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Forrige</Button>
        {step < 3 ? (
          <Button className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" disabled={!canNext} onClick={() => setStep((s) => s + 1)} data-testid="next-step-btn">Næste <ArrowRight className="h-4 w-4 ml-1" /></Button>
        ) : (
          <Button className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" disabled={saving} onClick={submit} data-testid="submit-project-btn">{saving ? "Opretter…" : "Opret projekt"} <Check className="h-4 w-4 ml-1" /></Button>
        )}
      </div>

      <AlertDialog open={warn} onOpenChange={setWarn}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader><AlertDialogTitle className="text-xl font-medium">Manglende ansvarlige</AlertDialogTitle>
            <AlertDialogDescription>{missingResponsible} projekttrin mangler en ansvarlig. Vil du fortsætte?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Tilbage</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl" onClick={doSubmit} data-testid="confirm-create-anyway">Fortsæt og opret</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
